import { useState, useEffect } from "react";
import { Settings } from "@/datasources/remote_datasource";
import { AppConfig } from "@/datasources/AppConfig";
import { decryptToken } from "@/utils/tokenCrypto";

/**
 * Resolve uma URL de mídia que pode vir relativa da API
 * para uma URL absoluta completa.
 *
 * - Se já for absoluta (http...), retorna como está.
 * - Se for relativa, prefixa com a URL base da API.
 *
 * IMPORTANTE: não modifica a query string de URLs absolutas, pois elas podem
 * ser SAS URLs do Azure Blob Storage e a assinatura `sig` é calculada sobre o
 * URL original. Qualquer alteração (incluindo cache-busting `?v=...`) invalida
 * a assinatura e faz o Azure retornar 403.
 */
export function resolveMediaUrl(arquivo?: string | null): string | undefined {
  if (!arquivo) return undefined;
  if (arquivo.startsWith("http")) return arquivo;
  const base = Settings.urlBase.replace(/\/$/, "");
  const path = arquivo.startsWith("/") ? arquivo : `/${arquivo}`;
  return `${base}${path}`;
}

/**
 * Retorna o host da API base para comparação de mesma origem.
 */
function getApiHost(): string {
  try {
    return new URL(Settings.urlBase).host;
  } catch {
    return "";
  }
}

/**
 * Determina se a URL aponta para storage externo (ex.: Azure Blob) e não
 * para a própria API. URLs externas pré-assinadas (SAS) não devem passar por
 * fetch autenticado nem receber query params extras.
 */
export function isExternalStorageUrl(imageUrl: string): boolean {
  try {
    return new URL(imageUrl).host !== getApiHost();
  } catch {
    return false;
  }
}

/**
 * Detecta URLs SAS do Azure Blob Storage pelo parâmetro `sig`.
 * Usado para garantir que nunca adicionemos cache-busting nessas URLs.
 */
export function isAzureSasUrl(imageUrl: string): boolean {
  try {
    const url = new URL(imageUrl);
    return url.searchParams.has("sig") && url.searchParams.has("sv");
  } catch {
    return false;
  }
}

/**
 * Busca o token de autenticação de várias fontes (fonte de verdade -> fallback).
 */
async function getAccessToken(): Promise<string | undefined> {
  if (AppConfig.tokenAcesso) return AppConfig.tokenAcesso;
  if (Settings.token?.accessToken) return Settings.token.accessToken;
  if (typeof localStorage !== "undefined") {
    const encrypted = localStorage.getItem("tokenAcesso");
    if (encrypted) {
      const token = await decryptToken(encrypted);
      if (token) return token;
    }
  }
  return undefined;
}

function getCsrfToken(): string {
  const key = "csrfToken";
  let token = "";
  try {
    token = localStorage.getItem(key) || "";
  } catch { /* cookies indisponíveis */ }
  if (!token) {
    token = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    try {
      localStorage.setItem(key, token);
    } catch { /* noop */ }
  }
  return token;
}

/**
 * Busca uma imagem via fetch (com token de autenticação) e retorna uma blob URL.
 * Útil quando a imagem está em um endpoint protegido e não pode ser acessada
 * diretamente via <img src>.
 */
export async function fetchImageAsBlobUrl(url: string): Promise<string> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {
    "client-id": AppConfig.appId,
    "x-csrf-token": getCsrfToken(),
  };
  if (token) {
    headers["authorization"] = `bearer ${token}`;
  }
  const response = await fetch(url, {
    headers,
    cache: "no-store",
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch image: ${response.status} ${response.statusText}`
    );
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

/**
 * Hook para carregar uma imagem.
 *
 * - data:/blob: -> usados diretamente.
 * - URLs de storage externo pré-assinadas (Azure SAS) -> usadas diretamente
 *   (o token está na query string; fetch quebraria por CORS).
 * - Endpoints protegidos da API (same-host) -> buscados com auth e convertidos
 *   em blob URL.
 */
export function useImageBlobUrl(imageUrl: string | undefined) {
  const [resolvedUrl, setResolvedUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!imageUrl) {
      setResolvedUrl(undefined);
      return;
    }

    // data: (base64) ou blob: -> usar diretamente
    if (imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")) {
      setResolvedUrl(imageUrl);
      return;
    }

    // Storage externo pré-assinado (Azure Blob SAS etc.) -> usar diretamente
    if (isExternalStorageUrl(imageUrl)) {
      setResolvedUrl(imageUrl);
      return;
    }

    // Endpoint protegido da API -> fetch autenticado + blob URL
    let objectUrl: string | undefined;
    let cancelled = false;

    fetchImageAsBlobUrl(imageUrl)
      .then((url) => {
        if (!cancelled) {
          objectUrl = url;
          setResolvedUrl(url);
        } else {
          URL.revokeObjectURL(url);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[useImageBlobUrl] Failed to load image:", imageUrl, err);
          setResolvedUrl(undefined);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [imageUrl]);

  return resolvedUrl;
}
